import { LoadState } from "@redotech/react-util/load";
import { getCompensationMethod } from "@redotech/redo-customer-portal-shared/return-review/util";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ReturnTotalCalculations as Calculations,
  CompensationMethod,
  CompensationMethodType,
  DraftReturn,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import {
  CombinedReturnItem,
  getBaseRepairFee,
  getCombinedReturnItem,
  getExpandedVariantExchangeProduct,
  getShipmentMethod,
  hasReturnItemsThatRequireShipping,
} from "@redotech/redo-model/draft-return/util";
import {
  getResource,
  Resource,
} from "@redotech/redo-model/localization/resource";
import { Provider } from "@redotech/redo-model/order";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { Payer, ProvisionType } from "@redotech/redo-model/return-flow";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { SubtotalCollapseSection } from "@redotech/redo-web/card";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { captializeWords } from "@redotech/string/util";
import { isValidEmail } from "@redotech/util/email";
import type { Stripe, StripeElements } from "@stripe/stripe-js";
import { Big } from "big.js";
import { memo, useContext, useState } from "react";
import { adjustmentName, isAddressComplete } from "../../util/util";
import * as review from "../review.module.css";
import { InstantExchangeModal } from "./instant-exchange-modal";

interface Subtotal {
  title: string;
  total: string;
}

export const SummaryCard = memo(function SummaryCard({
  copyResource,
  draftReturn,
  settings,
  newOrderMethod,
  stripe,
  paymentInfoFilled,
  elements,
  submitReturn,
  creatingStripePayment,
  submitReturnLoad,
  calculations,
}: {
  copyResource: Resource;
  draftReturn: DraftReturn;
  settings: ReturnAppSettings;
  newOrderMethod: ProvisionType;
  stripe: Stripe;
  paymentInfoFilled: boolean;
  elements: StripeElements | undefined;
  submitReturn: () => void;
  creatingStripePayment: boolean;
  submitReturnLoad: LoadState<void | undefined>;
  calculations: Calculations;
}) {
  const { formatCurrency, formatCurrencyFromUSD } = useContext(CurrencyContext);

  const [instantExchangeModalOpen, setInstantExchangeModalOpen] =
    useState<boolean>(false);

  const compensationMethod = getCompensationMethod(draftReturn);
  const {
    totalTaxes,
    totalRefund,
    totalStoreCredit,
    shippingFee,
    pickupFee,
    settlementAccept,
    settlementRefund,
    returnValue,
    newOrderValue,
    totalNewOrderTaxes,
    paymentOwed,
    paymentOwedRange,
    totalReturnValue,
    labelDeducted,
    totalRepairFee,
  } = calculations;

  const discountCodes = draftReturn?.newOrderData?.discountCodes
    ?.map((code) => code.replace(/-RETURN/g, ""))
    .filter((code) => code && code !== "Exchange Credit");

  const nonAccruableCreditAvailable = Object.values(
    compensationMethod.itemValues,
  ).reduce((acc, { exchangeCredit }) => acc.plus(exchangeCredit), new Big(0));

  const valueDifference = new Big(newOrderValue).minus(returnValue);
  const usedNonAccruableCredit = valueDifference.lt(0)
    ? 0
    : valueDifference.gt(nonAccruableCreditAvailable)
      ? nonAccruableCreditAvailable.toNumber()
      : valueDifference.toNumber();

  const adjustmentValue = Object.values(compensationMethod?.adjustments || {})
    .reduce((acc, adjustment) => acc.plus(adjustment), new Big(0))
    .plus(usedNonAccruableCredit)
    .toNumber();

  const shouldShowAdjustment =
    adjustmentValue !== 0 &&
    compensationMethod.type !== CompensationMethodType.REPAIR;

  const shipmentErrorOccurred = getShipmentErrorOccurred(draftReturn);

  const taxSubtotal = getTaxSubTotal(totalTaxes, formatCurrency);
  const newOrderTaxSubtotal = getNewOrderTaxes(
    totalNewOrderTaxes,
    formatCurrency,
  );
  const productSubtotals = getProductSubtotals(draftReturn, formatCurrency);
  const expandedVariantExchangeProductSubtotals =
    getExpandedVariantExchangeProductSubtotals(draftReturn, formatCurrency);
  const newOrderItemsSubtotals = getNewOrderItemsSubtotals(
    draftReturn,
    formatCurrency,
  );
  const newVariantExchangeItemSubtotals = getNewVariantExchangeItemSubtotals(
    draftReturn,
    formatCurrency,
    compensationMethod,
  );

  return (
    <>
      <div className={review.summaryCard}>
        <div className={review.summaryHeader}>Summary</div>
        <SubtotalCollapseSection
          header={{
            title: copyResource.FEES_HEADER,
            total: formatCurrency(returnValue),
          }}
          subtitleTextClassName={review.summaryTextLight}
          subtotals={[
            ...productSubtotals,
            ...expandedVariantExchangeProductSubtotals,
            ...taxSubtotal,
          ]}
          titleTextClassName={review.summaryText}
        />
        {newOrderValue > 0 && (
          <SubtotalCollapseSection
            header={{
              title: copyResource.FEES_ORDER_HEADER,
              total: formatCurrency(-newOrderValue), // Show the new order value as negative
            }}
            subtitleTextClassName={review.summaryTextLight}
            subtotals={[
              ...newOrderItemsSubtotals,
              ...newVariantExchangeItemSubtotals,
              ...expandedVariantExchangeProductSubtotals,
              ...newOrderTaxSubtotal,
            ]}
            titleTextClassName={review.summaryText}
          />
        )}
        {shouldShowAdjustment && (
          <div className={review.summaryCardItem}>
            <div className={review.summaryText}>
              {captializeWords(
                adjustmentName(settings.returnAdjustment, adjustmentValue),
              )}
            </div>
            <div className={review.summaryText}>
              {formatCurrency(adjustmentValue)}
            </div>
          </div>
        )}
        {settlementAccept && (
          <div className={review.summaryCardItem}>
            <div className={review.summaryText}>
              Special offer “You Keep It”
            </div>
            <div className={review.summaryText}>
              -{formatCurrency(returnValue * (1 - (settlementRefund ?? 0)))}
            </div>
          </div>
        )}
        {labelDeducted && (
          <>
            <div className={review.summaryCardItem}>
              <div className={review.summaryText}>Return shipping</div>
              <div className={review.summaryText}>
                {settlementAccept ? "⸺" : formatCurrency(shippingFee)}
              </div>
            </div>
            {pickupFee && (
              <div className={review.summaryCardItem}>
                <div className={review.summaryText}>Package pickup</div>
                <div className={review.summaryText}>
                  -{formatCurrencyFromUSD(pickupFee)}
                </div>
              </div>
            )}
          </>
        )}
        {getBaseRepairFee(totalRepairFee) > 0 && (
          <div className={review.summaryCardItem}>
            <div className={review.summaryText}>Repair fee</div>
            {totalRepairFee[0] !== totalRepairFee[1] ? (
              <div className={review.summaryText}>
                {`${formatCurrency(totalRepairFee[0])} - ${formatCurrency(totalRepairFee[1])}`}
              </div>
            ) : (
              <div className={review.summaryText}>
                {formatCurrency(getBaseRepairFee(totalRepairFee))}
              </div>
            )}
          </div>
        )}
        <hr className={review.line} />
        {paymentOwedRange ? (
          <div className={review.summaryCardItemPayment}>
            <div className={review.summaryTextBold}>Payment owed</div>
            <div className={review.summaryText}>
              {paymentOwedRange[0] !== paymentOwedRange[1]
                ? `${formatCurrency(paymentOwedRange[0])} - ${formatCurrency(paymentOwedRange[1])}`
                : formatCurrency(getBaseRepairFee(paymentOwedRange))}
            </div>
          </div>
        ) : newOrderMethod === ProvisionType.PROCESSED &&
          (paymentOwed > 0 || (totalRefund === 0 && totalStoreCredit === 0)) ? (
          <div className={review.summaryCardItemPayment}>
            <div className={review.summaryTextBold}>Payment owed</div>
            <div className={review.summaryText}>
              {formatCurrency(paymentOwed)}
            </div>
          </div>
        ) : null}
        {/* The 2 sections below should never be shown if payment owed > 0 since they are deducted from the amount first. */}
        {totalStoreCredit > 0 && (
          <div className={review.summaryCardItem}>
            <div className={review.summaryTextBold}>Store credit</div>
            <div className={review.summaryText}>
              {formatCurrency(totalStoreCredit)}
            </div>
          </div>
        )}
        {totalRefund > 0 && (
          <div className={review.summaryCardItem}>
            <div className={review.summaryTextBold}>Refund owed</div>
            <div className={review.summaryText}>
              {formatCurrency(totalRefund)}
            </div>
          </div>
        )}
        {((draftReturn?.newOrderItems?.length &&
          draftReturn.newOrderItems.length > 0) ||
          Object.values(compensationMethod?.itemValues ?? {}).some(
            (item) => item.isVariantExchange,
          )) &&
          newOrderMethod === ProvisionType.INSTANT && (
            <div>
              <div className={review.summaryCardItem}>
                <div className={review.summaryTextBold}>Payment owed</div>
                {newOrderValue > totalReturnValue ? (
                  // TODO: non usd currency
                  <div className={review.summaryText}>
                    {formatCurrency(newOrderValue - totalReturnValue)}
                  </div>
                ) : (
                  <div className={review.summaryText}>{formatCurrency(0)}</div>
                )}
              </div>
            </div>
          )}
        {!!draftReturn?.newOrderItems?.length &&
          discountCodes &&
          discountCodes.length > 0 && (
            <div>
              <div className={review.summaryCardItem}>
                <div className={review.summaryText}>Discount codes</div>
                <div className={review.summaryText}>
                  {discountCodes.join(", ")}
                </div>
              </div>
            </div>
          )}
        {needsSummarySubtext(
          draftReturn,
          newOrderMethod,
          calculations,
          shipmentErrorOccurred,
          compensationMethod,
        ) && (
          <div className={review.summarySubtext}>
            {getSummarySubtext(
              draftReturn,
              paymentOwed,
              compensationMethod,
              totalStoreCredit,
              totalRefund,
              getBaseRepairFee(totalRepairFee),
              settings,
            )}
          </div>
        )}
        {shipmentErrorOccurred && (
          <div className={review.error}>
            Unable to calculate shipping cost. Please contact customer support.
          </div>
        )}
        <div className={review.button}>
          <Button
            disabled={canSubmit(
              calculations,
              draftReturn,
              compensationMethod,
              newOrderMethod,
              stripe,
              elements,
              paymentInfoFilled,
              shipmentErrorOccurred,
            )}
            onClick={async function (e) {
              if (newOrderMethod === ProvisionType.INSTANT) {
                setInstantExchangeModalOpen(true);
              } else {
                submitReturn();
              }
            }}
            pending={creatingStripePayment || submitReturnLoad.pending}
            style={{ width: "100%" }}
            theme={ButtonTheme.PRIMARY}
            type="submit"
          >
            {copyResource.REVIEW_SUBMIT_BUTTON_TEXT}
          </Button>
        </div>
      </div>
      <InstantExchangeModal
        creatingStripePayment={creatingStripePayment}
        instantExchangeModalOpen={instantExchangeModalOpen}
        setInstantExchangeModalOpen={setInstantExchangeModalOpen}
        settings={settings}
        submitReturn={submitReturn}
      />
    </>
  );
});

function getSummarySubtext(
  draftReturn: DraftReturn,
  paymentOwed: number,
  compensationMethod: CompensationMethod,
  totalStoreCredit: number,
  totalRefund: number,
  totalRepairFee: number,
  settings: ReturnAppSettings,
) {
  if (
    draftReturn.returnType === ReturnTypeEnum.CLAIM &&
    settings.theme?.claimSummarySubtext
  ) {
    return settings.theme.claimSummarySubtext;
  }
  if (
    draftReturn.returnType === ReturnTypeEnum.RETURN &&
    settings.theme?.returnSummarySubtext
  ) {
    return settings.theme.returnSummarySubtext;
  }
  if (
    draftReturn.returnType === ReturnTypeEnum.WARRANTY &&
    settings.theme?.warrantySummarySubtext
  ) {
    return settings.theme.warrantySummarySubtext;
  }

  const copyResource = getResource({
    overrides: settings.resourceOverride,
    variant: draftReturn.returnType,
  });

  let refundText = `Once your ${copyResource.TYPE_DISPLAY_NAME} is processed`;

  if (paymentOwed > 0 || compensationMethod?.chargeNewOrderShipping) {
    refundText += ", you will receive an email to pay for ";
    if (paymentOwed <= 0) {
      refundText += "the shipping for ";
    }
    if (totalRepairFee && settings.warranties?.repairFeeEnabled) {
      refundText += "the repair order";
    } else {
      refundText += "your new order";
    }
  } else if (totalStoreCredit > 0) {
    refundText += ", you will receive an email with your store credit";
  } else if (totalRefund > 0) {
    refundText += ", you will receive a refund to your original payment method";
  } else if (totalRepairFee && settings.warranties?.repairFeeEnabled) {
    refundText += ", you will receive an email to pay for the repair";
  }
  refundText += ".";

  return refundText;
}

const canSubmit = (
  calculations: Calculations,
  draftReturn: DraftReturn,
  compensationMethod: CompensationMethod,
  newOrderMethod: ProvisionType,
  stripe: Stripe,
  elements: StripeElements | undefined,
  paymentInfoFilled: boolean,
  shipmentErrorOccurred: boolean,
): boolean => {
  const {
    shippingFee,
    pickupFee,
    showNewItems,
    hasNonGreenReturnItems,
    skipPayment,
    labelDeducted,
  } = calculations;

  const needsAddressButIncomplete =
    !isAddressComplete(draftReturn.customer?.shippingAddress) &&
    (hasNonGreenReturnItems || showNewItems);
  const invalidEmail = !isValidEmail(
    draftReturn.customer?.shippingAddress?.email ?? "",
  );
  const hasPickupFee = !!pickupFee;
  const needsPickupPayment = hasPickupFee && !labelDeducted;
  const needsLabelPayment =
    !skipPayment &&
    shippingFee !== 0 &&
    compensationMethod.payer === Payer.CUSTOMER;
  const needsInstantPayment = newOrderMethod === ProvisionType.INSTANT;
  const needsPayment =
    needsLabelPayment || needsInstantPayment || needsPickupPayment;
  const invalidStripe = !stripe || !elements || !paymentInfoFilled;
  const needsPaymentButInvalidStripe = needsPayment && invalidStripe;

  return (
    needsAddressButIncomplete ||
    invalidEmail ||
    needsPaymentButInvalidStripe ||
    shipmentErrorOccurred
  );
};

const getShipmentErrorOccurred = (draftReturn: DraftReturn): boolean => {
  const shipmentMethod = getShipmentMethod(draftReturn);
  const shippingRequired = hasReturnItemsThatRequireShipping(draftReturn);

  return (
    shippingRequired &&
    (shipmentMethod === ShipmentMethodName.SELF_SHIP ||
      shipmentMethod === ShipmentMethodName.PICKUP) &&
    !draftReturn.shipments?.length
  );
};

const getTaxSubTotal = (
  totalTaxes: number,
  formatCurrency: (value: number) => string,
): Subtotal[] => {
  return totalTaxes
    ? [{ title: "Taxes", total: formatCurrency(totalTaxes) }]
    : [];
};

const getNewOrderTaxes = (
  totalNewOrderTaxes: number,
  formatCurrency: (value: number) => string,
): Subtotal[] => {
  return totalNewOrderTaxes
    ? [{ title: "Taxes", total: formatCurrency(totalNewOrderTaxes) }]
    : [];
};

const needsSummarySubtext = (
  draftReturn: DraftReturn,
  newOrderMethod: ProvisionType,
  calculations: Calculations,
  shipmentErrorOccurred: boolean,
  compensationMethod: CompensationMethod,
): boolean => {
  const { totalRefund, totalStoreCredit, paymentOwed } = calculations;

  return (
    newOrderMethod !== ProvisionType.INSTANT &&
    !shipmentErrorOccurred &&
    draftReturn.orders?.[0]?.platform !== Provider.COMMERCE_CLOUD &&
    (paymentOwed > 0 ||
      compensationMethod?.chargeNewOrderShipping ||
      totalStoreCredit > 0 ||
      totalRefund > 0)
  );
};

const getProductSubtotals = (
  draftReturn: DraftReturn,
  formatCurrency: (amount: number | string) => string,
): Subtotal[] => {
  return draftReturn.pendingReturnItems.flatMap((pendingReturnItem) => {
    const combinedItem = getCombinedReturnItem(
      draftReturn,
      pendingReturnItem.id,
    );

    return getNonExpandedProducts(combinedItem, formatCurrency);
  });
};

const getExpandedVariantExchangeProductSubtotals = (
  draftReturn: DraftReturn,
  formatCurrency: (amount: number | string) => string,
): Subtotal[] => {
  return draftReturn.pendingReturnItems.flatMap((pendingReturnItem) => {
    const combinedItem = getCombinedReturnItem(
      draftReturn,
      pendingReturnItem.id,
    );

    return (
      combinedItem.pendingReturnItem?.expandedProductVariantExchanges?.flatMap(
        (variantExchange) => {
          const product = getExpandedVariantExchangeProduct(
            combinedItem.returnableItem,
            variantExchange?.productId,
          );
          if (!product) {
            return [];
          }
          return [
            { title: product.title, total: formatCurrency(product.price) },
          ];
        },
      ) || []
    );
  });
};

const getNewOrderItemsSubtotals = (
  draftReturn: DraftReturn,
  formatCurrency: (amount: number | string) => string,
): Subtotal[] => {
  return (draftReturn?.newOrderData?.items || []).map((item, index) => ({
    // Show "x{number of items}" next to the title if more than one is being ordered
    title: item.title + (item.quantity > 1 ? ` x ${item.quantity}` : ""),
    // Make sure global discounts are applied to the individual items in the summary dropdown
    total: formatCurrency(parseFloat(item?.itemValue ?? "0")),
  }));
};

const getNewVariantExchangeItemSubtotals = (
  draftReturn: DraftReturn,
  formatCurrency: (amount: number | string) => string,
  compensationMethod: CompensationMethod,
): Subtotal[] => {
  return Object.entries(compensationMethod.itemValues)
    .flatMap(([id, item]) => {
      if (!item.isVariantExchange) {
        return null;
      }
      return getNonExpandedProducts(
        getCombinedReturnItem(draftReturn, id),
        formatCurrency,
      );
    })
    .filter((item) => item != null);
};

const getNonExpandedProducts = (
  combinedItem: CombinedReturnItem,
  formatCurrency: (amount: number | string) => string,
): Subtotal[] => {
  return combinedItem.returnableItem.products.reduce<Subtotal[]>(
    (acc, product) => {
      const returnQuantity = combinedItem.pendingReturnItem?.quantity ?? 1;
      if (
        combinedItem.returnableItem.isExpanded &&
        !product?.isUnbundled &&
        !product?.isExpandedPrimaryProduct
      ) {
        return acc;
      } else if (
        combinedItem.returnableItem.isExpanded &&
        product?.isExpandedPrimaryProduct &&
        (combinedItem.pendingReturnItem?.expandedProductVariantExchanges || [])
          .length > 0
      ) {
        return acc;
      }
      acc.push({
        title:
          returnQuantity > 1
            ? `${product.title} x ${returnQuantity}`
            : product.title,
        total: formatCurrency(+product.returnValue * returnQuantity),
      });
      return acc;
    },
    [],
  );
};
